import { useCallback, useEffect } from 'react';
import { useGlobalSearchParams, useRouter } from 'expo-router';

import { ConflictType } from '@sg/graphql-schema';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';

import { useBagCount } from '../../../hooks';
import { useCartHandoffMutation, type CartHandoffMutation } from '../graphql/CartHandoff.generated';

/**
 * Hook to handle cart handoff from a URL parameter.
 * If successful, removes the orderToken from the URL and navigates to the bag screen.
 * @see {@link https://sweetgreen.atlassian.net/wiki/spaces/TD/pages/7249297413/Agentic+Ordering}
 */
export const useCartHandoff = () => {
  const router = useRouter();
  const { track } = useTelemetry();

  // ─── State ─────────────────────────────────────────────────────────────────

  const isFeatureEnabled = useFeatureFlag('CELS-5511-cart-handoff-enabled');

  const { orderToken } = useGlobalSearchParams<{ orderToken?: string }>();
  const hasCartHandoffRequest = isFeatureEnabled && Boolean(orderToken);

  const { bagCount, isLoadingBagCount } = useBagCount();
  const hasActiveBag = bagCount > 0;

  // ─── Mutation ──────────────────────────────────────────────────────────────

  const [response, handoffCart] = useCartHandoffMutation();
  const result = response.data?.handoffCart;
  const reorderConflicts = useReorderConflicts(result);

  const isHandingOffCart = response.fetching;
  const hasConflicts = reorderConflicts.length > 0;
  const hasHandoffCompleted = result?.__typename === 'ReorderSuccess';
  const isReorderUnavailable = checkReorderAvailability(result);

  const startCartHandoff = useCallback(async () => {
    if (!isFeatureEnabled || !orderToken) return;

    track('app.cart-handoff.start', { orderToken });

    const ignoreConflicts = reorderConflicts.length > 0;
    const result = await handoffCart({ orderToken, ignoreConflicts });
    const payload = result.data?.handoffCart;
    const hasHandoffCompleted = payload?.__typename === 'ReorderSuccess';

    if (hasHandoffCompleted) {
      track('app.cart-handoff.success', { orderToken });
      router.setParams({ orderToken: undefined });
      router.navigate('/bag');
      return;
    }

    track('app.cart-handoff.failure', {
      orderToken,
      failureReason: payload?.__typename,
    });
  }, [handoffCart, router, track, reorderConflicts, isFeatureEnabled, orderToken]);

  const cancelCartHandoff = useCallback(() => {
    router.setParams({ orderToken: undefined });
  }, [router]);

  // ─── Automatic Handoff ─────────────────────────────────────────────────────

  const canTriggerAutomaticHandoff = !isLoadingBagCount && bagCount === 0 && !result;

  useEffect(() => {
    if (!hasCartHandoffRequest || !canTriggerAutomaticHandoff) return;
    void startCartHandoff();
  }, [hasCartHandoffRequest, canTriggerAutomaticHandoff, startCartHandoff]);

  // ───────────────────────────────────────────────────────────────────────────

  return {
    hasCartHandoffRequest,
    hasActiveBag,
    hasConflicts,
    isHandingOffCart,
    hasHandoffCompleted,
    isReorderUnavailable,
    reorderConflicts,
    startCartHandoff,
    cancelCartHandoff,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const useReorderConflicts = (payload: CartHandoffMutation['handoffCart'] | undefined) => {
  if (payload?.__typename === 'ReorderConflict') {
    return [...new Set(payload.conflicts.flatMap(classifyConflict))];
  }

  return [];
};

function classifyConflict(conflict: ReorderConflictPayload['conflicts'][number]) {
  if (conflict.type === ConflictType.IngredientUnavailable) {
    const ingredientNames = conflict.unavailableIngredients.map(({ name }) => name);
    return ingredientNames.length > 0 ? ingredientNames : [conflict.product.name];
  }

  if (conflict.type === ConflictType.ProductUnavailable) {
    return [conflict.product.name];
  }

  return [];
}

function checkReorderAvailability(payload: CartHandoffMutation['handoffCart'] | undefined) {
  if (!payload || payload.__typename === 'ReorderSuccess') return false;

  return (
    payload.__typename !== 'ReorderConflict' ||
    payload.isNothingAvailable ||
    payload.conflicts.length === 0
  );
}

// ─── Types ───────────────────────────────────────────────────────────────────

type ReorderConflictPayload = Extract<
  CartHandoffMutation['handoffCart'],
  { __typename: 'ReorderConflict' }
>;
